import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Shield, LogOut, CheckCircle2, XCircle, Zap, Clock, Trash2, Search, RefreshCw, ChefHat, Layers, Plus, Save, Settings2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = { pending: "In attesa", trial: "Trial 3gg", full: "Full", voice_only: "Voice only", expired: "Scaduto" };
const PLAN_COLORS = { pending: "#6B625B", trial: "#D99B26", full: "#3B503A", voice_only: "#C85A32", expired: "#8B0000" };

function fmtDate(iso) { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }); } catch { return "—"; } }
function daysLeft(iso) { if (!iso) return null; const ms = new Date(iso) - new Date(); return ms <= 0 ? 0 : Math.ceil(ms / 86400000); }

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("users"); // users | plans
  const [expanded, setExpanded] = useState(null);
  const [newPlan, setNewPlan] = useState({ name: "", price_eur: 3, duration_days: 30, features: { voice: true, ordering: false, reservations: false, party: false, chat: false, notify: false } });

  const token = localStorage.getItem("vm_admin_token");
  const email = localStorage.getItem("vm_admin_email");

  useEffect(() => { if (!token) navigate("/admin/login"); }, [token, navigate]);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        axios.get(`${API}/api/admin/users`, auth),
        axios.get(`${API}/api/admin/plans`, auth),
      ]);
      setUsers(u.data); setPlans(p.data.plans);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("vm_admin_token"); navigate("/admin/login"); }
      else toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const activate = async (u) => {
    try { await axios.post(`${API}/api/admin/users/${u.user_id}/activate`, {}, auth); toast.success(`${u.email} attivato · trial 3gg`); load(); }
    catch { toast.error("Errore attivazione"); }
  };
  const setPlan = async (u, plan, days = 30) => {
    try { await axios.put(`${API}/api/admin/users/${u.user_id}/plan`, { plan, subscription_days: days }, auth); toast.success(`Piano ${PLAN_LABELS[plan]} applicato`); load(); }
    catch { toast.error("Errore aggiornamento"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}? Non reversibile.`)) return;
    try { await axios.delete(`${API}/api/admin/users/${u.user_id}`, auth); toast.success("Utente eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const assignPlan = async (u, plan_id) => {
    try { await axios.put(`${API}/api/admin/users/${u.user_id}/assign-plan`, { plan_id }, auth); toast.success("Piano assegnato"); load(); }
    catch { toast.error("Errore"); }
  };
  const setFeatures = async (u, features) => {
    try { await axios.put(`${API}/api/admin/users/${u.user_id}/features`, { features }, auth); toast.success("Servizi aggiornati"); load(); }
    catch { toast.error("Errore"); }
  };
  const savePlan = async (p) => {
    try { await axios.put(`${API}/api/admin/plans/${p.id}`, { name: p.name, price_eur: Number(p.price_eur), duration_days: Number(p.duration_days), features: p.features }, auth); toast.success("Piano salvato"); load(); }
    catch { toast.error("Errore salvataggio"); }
  };
  const createPlan = async () => {
    if (!newPlan.name.trim()) { toast.error("Nome piano obbligatorio"); return; }
    try { await axios.post(`${API}/api/admin/plans`, { ...newPlan, price_eur: Number(newPlan.price_eur), duration_days: Number(newPlan.duration_days) }, auth); toast.success("Piano creato"); setNewPlan({ name: "", price_eur: 3, duration_days: 30, features: { voice: true, ordering: false, reservations: false, party: false, chat: false, notify: false } }); load(); }
    catch { toast.error("Errore creazione"); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Eliminare piano "${p.name}"?`)) return;
    try { await axios.delete(`${API}/api/admin/plans/${p.id}`, auth); toast.success("Piano eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const updatePlanLocal = (id, patch) => setPlans((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p));
  const updatePlanFeature = (id, key, val) => setPlans((prev) => prev.map((p) => p.id === id ? { ...p, features: { ...p.features, [key]: val } } : p));

  const logout = () => { localStorage.removeItem("vm_admin_token"); localStorage.removeItem("vm_admin_email"); navigate("/admin/login"); };

  const filtered = users.filter((u) => {
    if (filter !== "all" && u.plan_status !== filter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.restaurant?.name || "").toLowerCase().includes(q);
  });

  const stats = useMemo(() => ({
    total: users.length,
    pending: users.filter((u) => u.plan_status === "pending").length,
    trial: users.filter((u) => u.plan_status === "trial").length,
    full: users.filter((u) => u.plan_status === "full").length,
    voice: users.filter((u) => u.plan_status === "voice_only").length,
    expired: users.filter((u) => u.plan_status === "expired").length,
  }), [users]);

  return (
    <div className="min-h-screen bg-[#161412] text-[#FBF9F5]">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D99B26] to-[#C85A32] grid place-items-center text-white"><Shield size={18}/></div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-base sm:text-lg leading-tight">Backoffice · VOICEMENU</div>
              <div className="text-[11px] text-[#B8AFA5]">{email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"><RefreshCw size={15}/></button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white"><LogOut size={16}/><span className="hidden sm:inline">Esci</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 space-y-5">
        <div className="flex gap-2">
          <button data-testid="admin-tab-users" onClick={() => setTab("users")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${tab === "users" ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10"}`}><ChefHat size={14}/> Utenti ({users.length})</button>
          <button data-testid="admin-tab-plans" onClick={() => setTab("plans")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${tab === "plans" ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10"}`}><Layers size={14}/> Piani ({plans.length})</button>
        </div>

        {tab === "plans" && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white/5 border-2 border-dashed border-white/20 p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#D99B26] font-bold flex items-center gap-1"><Plus size={11}/> Nuovo piano</div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input data-testid="new-plan-name" value={newPlan.name} onChange={(e) => setNewPlan({...newPlan, name: e.target.value})} placeholder="Nome" className="rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/40"/>
                <input data-testid="new-plan-price" type="number" step="0.5" value={newPlan.price_eur} onChange={(e) => setNewPlan({...newPlan, price_eur: e.target.value})} placeholder="€" className="rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white"/>
                <input data-testid="new-plan-days" type="number" value={newPlan.duration_days} onChange={(e) => setNewPlan({...newPlan, duration_days: e.target.value})} placeholder="giorni" className="rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm text-white"/>
                <button data-testid="new-plan-save" onClick={createPlan} className="rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white font-bold text-sm">Crea</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["voice","ordering","reservations","party","chat","notify"].map((f) => (
                  <label key={f} className="inline-flex items-center gap-1.5 text-[11px] text-white/80 bg-white/5 rounded-full px-2.5 py-1">
                    <input type="checkbox" checked={newPlan.features[f]} onChange={(e) => setNewPlan({...newPlan, features: {...newPlan.features, [f]: e.target.checked}})} className="accent-[#D99B26]"/>
                    {f}
                  </label>
                ))}
              </div>
            </div>
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-row-${p.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <input value={p.name} onChange={(e) => updatePlanLocal(p.id, { name: e.target.value })} className="flex-1 min-w-[150px] rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm font-bold text-white"/>
                  <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm">€ <input type="number" step="0.5" value={p.price_eur} onChange={(e) => updatePlanLocal(p.id, { price_eur: e.target.value })} className="w-16 bg-transparent outline-none text-white"/></div>
                  <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-2 text-sm"><input type="number" value={p.duration_days} onChange={(e) => updatePlanLocal(p.id, { duration_days: e.target.value })} className="w-14 bg-transparent outline-none text-white"/> gg</div>
                  <button data-testid={`plan-save-${p.id}`} onClick={() => savePlan(p)} className="inline-flex items-center gap-1 rounded-full bg-[#3B503A] hover:bg-[#2f4030] text-white text-xs font-bold px-3 py-2"><Save size={12}/> Salva</button>
                  {!["plan_voice","plan_full","plan_trial"].includes(p.id) && <button data-testid={`plan-delete-${p.id}`} onClick={() => deletePlan(p)} className="rounded-full bg-red-900/40 hover:bg-red-900/60 text-red-200 text-xs px-3 py-2"><Trash2 size={12}/></button>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["voice","ordering","reservations","party","chat","notify"].map((f) => (
                    <label key={f} className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1 cursor-pointer ${p.features?.[f] ? "bg-[#3B503A]/60 text-white" : "bg-white/5 text-white/60"}`}>
                      <input data-testid={`plan-feat-${p.id}-${f}`} type="checkbox" checked={!!p.features?.[f]} onChange={(e) => updatePlanFeature(p.id, f, e.target.checked)} className="accent-[#D99B26]"/>
                      {f}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "users" && (
        <>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          {[
            { key: "total", label: "Totale", value: stats.total, color: "#FBF9F5" },
            { key: "pending", label: "In attesa", value: stats.pending, color: PLAN_COLORS.pending },
            { key: "trial", label: "In trial", value: stats.trial, color: PLAN_COLORS.trial },
            { key: "full", label: "Full", value: stats.full, color: PLAN_COLORS.full },
            { key: "voice_only", label: "Voice", value: stats.voice, color: PLAN_COLORS.voice_only },
            { key: "expired", label: "Scaduti", value: stats.expired, color: PLAN_COLORS.expired },
          ].map((s) => (
            <button key={s.key} data-testid={`stat-${s.key}`} onClick={() => setFilter(s.key === "total" ? "all" : s.key)} className={`text-left rounded-2xl p-3 border transition ${filter === (s.key === "total" ? "all" : s.key) ? "bg-white/10 border-white/40" : "bg-white/5 border-white/10 hover:bg-white/10"}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: s.color }}>{s.label}</div>
              <div className="font-serif-display text-2xl font-bold text-white mt-0.5">{s.value}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-white/5 rounded-full px-4 py-2 border border-white/10">
          <Search size={14} className="text-white/50"/>
          <input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per email, nome o ristorante..." className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/40"/>
        </div>

        {loading ? <div className="text-white/60 text-center py-10">Caricamento...</div> : filtered.length === 0 ? (
          <div className="text-white/60 text-center py-16 bg-white/5 rounded-2xl">Nessun utente</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((u) => {
              const days = daysLeft(u.trial_ends_at || u.subscription_ends_at);
              return (
                <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0"/> : <div className="w-10 h-10 rounded-full bg-white/10 grid place-items-center flex-shrink-0"><ChefHat size={16}/></div>}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{u.name || "—"}</div>
                        <div className="text-[11px] text-white/60 font-mono truncate">{u.email}</div>
                        {u.restaurant && <div className="text-[11px] text-[#D99B26] truncate">🍴 {u.restaurant.name} · /{u.restaurant.slug}</div>}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-white flex-shrink-0" style={{ background: PLAN_COLORS[u.plan_status] }}>{PLAN_LABELS[u.plan_status]}</span>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-white/70">
                    <div className="inline-flex items-center gap-1"><Clock size={11}/> Iscritto: {fmtDate(u.created_at)}</div>
                    {days != null && <div className="inline-flex items-center gap-1 text-[#D99B26]"><Zap size={11}/> {days} giorni residui</div>}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {!u.activated && <button data-testid={`activate-${u.user_id}`} onClick={() => activate(u)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white text-[11px] font-bold"><CheckCircle2 size={12}/> Attiva (trial 3gg)</button>}
                    {plans.length > 0 && (
                      <select data-testid={`assign-plan-${u.user_id}`} onChange={(e) => e.target.value && assignPlan(u, e.target.value)} defaultValue="" className="rounded-full bg-white/10 border border-white/20 text-white text-[11px] font-bold px-2 py-1.5">
                        <option value="" disabled>Assegna piano...</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · €{p.price_eur} · {p.duration_days}gg</option>)}
                      </select>
                    )}
                    <button data-testid={`toggle-features-${u.user_id}`} onClick={() => setExpanded(expanded === u.user_id ? null : u.user_id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold"><Settings2 size={12}/> Servizi</button>
                    {u.activated && u.plan_status !== "expired" && <button data-testid={`plan-expired-${u.user_id}`} onClick={() => setPlan(u, "expired")} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-[11px] font-bold inline-flex items-center gap-1"><XCircle size={11}/> Scadi</button>}
                    <button data-testid={`delete-${u.user_id}`} onClick={() => del(u)} className="px-3 py-1.5 rounded-full bg-red-900/40 hover:bg-red-900/60 text-red-200 text-[11px] font-bold inline-flex items-center gap-1"><Trash2 size={11}/></button>
                  </div>

                  {expanded === u.user_id && (
                    <div className="mt-3 p-3 rounded-xl bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#D99B26] font-bold mb-2">Servizi attivi per {u.email}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {["voice","ordering","reservations","party","chat","notify"].map((f) => {
                          const on = !!u.features?.[f];
                          return (
                            <button key={f} data-testid={`feature-toggle-${u.user_id}-${f}`} onClick={() => setFeatures(u, { ...u.features, [f]: !on })} className={`inline-flex items-center justify-between gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold ${on ? "bg-[#3B503A] text-white" : "bg-white/5 text-white/50"}`}>
                              <span>{ {voice:"🎙 Menu AI", ordering:"🛒 Ordini", reservations:"📅 Prenotazioni", party:"🏆 Quiz", chat:"💬 Chat", notify:"🔔 Notifiche"}[f] }</span>
                              <span className={`w-2 h-2 rounded-full ${on ? "bg-[#D99B26]" : "bg-white/20"}`}/>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
